import {
  type CombinedStyleSelectorName,
  type SettingsPropsParametricSelector,
  combinedSettingsSelector,
  combinedStyleSelector,
  createShallowEqualSelector,
  getBackgroundHoverSettingSelector,
  getBorderHoverSettingSelector,
  getCornersRadiusSelector,
  getInheritedTextHoverSettingSelector,
  getSettingValueSelector,
  makeTextStyleSelector as makeTextStyleSelectorCommon
} from "@client/selectors";
import { BackgroundType } from "@client/types";
import * as React from "react";
import { createSelector } from "reselect";

const makeStyleBaseSelector = (target: string, prefix?: string, excludeMargin?: boolean) => {
  const baseStyleSelectorNames: CombinedStyleSelectorName[] = ["background", "border", "boxShadow"];
  if (!excludeMargin) {
    baseStyleSelectorNames.push("margin");
  }
  const baseStyleSelector = combinedStyleSelector(target, baseStyleSelectorNames, prefix);

  const paddingSettingSelector = getSettingValueSelector(target, "padding", prefix);
  const sizeSettingSelector = getSettingValueSelector(target, "size", prefix);
  const paddingSelector = createSelector([paddingSettingSelector, sizeSettingSelector], (padding, size) => {
    switch (size) {
      case "btn-lg":
      case "large": {
        return "20px 30px";
      }
      case "btn-def-size":
      case "medium": {
        return "10px 20px";
      }
      case "btn-xs":
      case "small": {
        return "5px 10px";
      }
      default:
        return padding;
    }
  });

  const radiusSelector = getCornersRadiusSelector(target, prefix);

  return createSelector(
    [baseStyleSelector, radiusSelector, paddingSelector],
    (style, radius, padding): React.CSSProperties => ({
      ...style,
      borderRadius: radius ?? "0px",
      padding,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    })
  );
};

const ALIGN_TO_ALIGN_SELF = {
  left: "flex-start",
  center: "center",
  right: "flex-end",
  justify: "stretch"
};
export const makeAlignStyleSelector = (target: string, prefix?: string) => {
  const alignSelfSelector = getSettingValueSelector(target, "innerHorizontalAlign", prefix);

  return createSelector(
    [alignSelfSelector],
    (alignSelf): React.CSSProperties => ({
      alignSelf: ALIGN_TO_ALIGN_SELF[alignSelf]
    })
  );
};

const stylePropsSelector = (state: any, props: { style?: React.CSSProperties }) => props.style;
export const makeStyleSelector = (target: string, prefix?: string, excludeMargin?: boolean) => {
  const cssVariablesSelector = makeCssVariablesSelector(target, prefix);
  const styleBaseSelector = makeStyleBaseSelector(target, prefix, excludeMargin);
  const textStyleSelector = makeTextStyleSelector(target, prefix);

  return createSelector(
    [styleBaseSelector, cssVariablesSelector, textStyleSelector, stylePropsSelector],
    (style, cssVariables, textStyle, styleProps): React.CSSProperties => ({
      ...style,
      ...cssVariables,
      ...textStyle,
      ...styleProps
    })
  );
};

export const makeStyleWithAlignSelector = (target: string, prefix?: string, excludeMargin?: boolean) => {
  const alignSelfStyleSelector = makeAlignStyleSelector(target, prefix);
  const styleSelector = makeStyleSelector(target, prefix, excludeMargin);

  return createSelector(
    [styleSelector, alignSelfStyleSelector],
    (style, alignSelfStyle): React.CSSProperties => ({
      ...style,
      ...alignSelfStyle
    })
  );
};

export const makeIconStyleSelector = (target: string, prefix?: string) =>
  combinedSettingsSelector(
    target,
    [
      { name: "color", prop: "color", path: "text", type: "color" },
      { name: "size", prop: "fontSize", path: "text" }
    ],
    prefix
  );

export const makeTextStyleSelector = (target: string, prefix?: string): SettingsPropsParametricSelector => {
  const iconStyleSelector = makeIconStyleSelector(target);
  const textStyleCommonSelector = makeTextStyleSelectorCommon(target, prefix);

  return createSelector([iconStyleSelector, textStyleCommonSelector], (iconStyle, textStyle) => {
    return {
      ...iconStyle,
      ...textStyle
    };
  });
};

export const makeCssVariablesSelector = (target: string, prefix?: string) => {
  const backgroundSettingSelector = getBackgroundHoverSettingSelector(target, prefix);
  const borderSettingSelector = getBorderHoverSettingSelector(target, prefix);
  const borderRadiusSelector = getCornersRadiusSelector(target, prefix);
  const textSettingSelector = getInheritedTextHoverSettingSelector(target, prefix);

  return createShallowEqualSelector(
    [backgroundSettingSelector, borderRadiusSelector, borderSettingSelector, textSettingSelector],
    (background, borderRadius, border, text): object => {
      return {
        ["--br"]: borderRadius,
        ["--bgc"]: background?.type === BackgroundType.Color ? background.color || "var(--color)" : "unset",
        ["--hbc"]: background?.type === BackgroundType.Color ? border?.hoverColor || "var(--color)" : "unset",
        ["--hbgc"]: background?.type === BackgroundType.Color ? background.hoverColor || "var(--color)" : "unset",
        ["--tc"]: text?.color || "var(--text-color)",
        ["--htc"]: text?.hoverColor || "var(--text-color)",
        ["--bop"]: background?.opacity ? parseInt(background.opacity) / 100 : "unset",
        ["--obgc"]:
          background?.overlay && background?.type === BackgroundType.Image
            ? background.overlay.color || "var(--color)"
            : "unset",
        ["--hobgc"]:
          background?.overlay && background?.type === BackgroundType.Image
            ? background.overlay.hoverColor || "var(--color)"
            : "unset",
        ["--oop"]: background?.overlay?.opacity ? parseInt(background.overlay.opacity) / 100 : "unset"
      };
    }
  );
};
